import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ExcelJS from "exceljs";
import "./PortfolioReviewView.css";

// PCC_PORTFOLIO_REVIEW_ENTERPRISE_V1
// PCC_PORTFOLIO_REVIEW_FINANCE_STABLE_V1

const ALL = "ALL";

function n(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function txt(value) {
  return String(value || "").trim();
}

function norm(value) {
  return txt(value).toUpperCase();
}

function getProjectId(project) {
  return (
    project?.id ||
    project?.proyectoId ||
    String(project?.pk || "").replace(/^PROJECT#/, "")
  );
}

function situationOf(project) {
  const situation = norm(project?.situacionProyecto);

  if (situation === "VENTA_ACTIVA") return "VENTA_ACTIVA";
  if (situation === "EJECUCION") return "EJECUCION";
  if (situation === "CERRADO") return "CERRADO";
  if (situation === "MUERTO") return "MUERTO";

  return "SIN_CLASIFICAR";
}

function situationLabel(value) {
  const map = {
    VENTA_ACTIVA: "Venta activa",
    EJECUCION: "En ejecución",
    CERRADO: "Cerrado",
    MUERTO: "Muerto",
    SIN_CLASIFICAR: "Sin clasificar",
  };

  return map[value] || value;
}

function commercialStage(project) {
  return norm(project?.etapaComercial);
}

function stageLabel(project) {
  const situation = situationOf(project);

  if (situation === "EJECUCION") {
    return "Ejecución";
  }

  if (
    situation === "CERRADO" ||
    situation === "MUERTO"
  ) {
    return situationLabel(situation);
  }

  const stage = commercialStage(project);

  const labels = {
    OPORTUNIDAD: "Oportunidad",
    CALIFICACION: "Calificación",
    ANALISIS: "Análisis",
    PROPUESTA: "Propuesta / Cotización",
    CONTRATACION: "Contratación",
    GANADO: "Ganado",
  };

  return labels[stage] || "Sin etapa";
}

function projectAmount(project) {
  const situation = situationOf(project);

  if (situation === "VENTA_ACTIVA") {
    return Math.max(
      0,
      n(
        project?.montoOportunidad ??
        project?.montoBaseTamano
      )
    );
  }

  return Math.max(
    0,
    n(
      project?.montoContratado ??
      project?.montoContrato ??
      project?.montoBaseTamano ??
      project?.montoOportunidad
    )
  );
}

function financialRoi(financial) {
  const invested = n(financial?.invertido);
  const utility = n(financial?.utilidad);

  if (invested <= 0) {
    return null;
  }

  return (utility / invested) * 100;
}

function roiBand(roi) {
  if (roi === null || roi === undefined) {
    return "NO_DATA";
  }

  if (roi <= 0) return "NEGATIVE";
  if (roi <= 10) return "LOW";
  if (roi <= 18) return "MEDIUM";

  return "HIGH";
}

function roiLabel(roi) {
  if (roi === null || roi === undefined) {
    return "Sin datos";
  }

  return `${roi.toFixed(1)}%`;
}

function sizeCode(project) {
  if (norm(project?.tipoProyecto) === "INHOUSE") {
    return "INHOUSE";
  }

  return norm(project?.tamanoProyecto) || "SIN_CLASIFICAR";
}

function sizeLabel(project) {
  const code = sizeCode(project);

  const labels = {
    CHICO: "Chico",
    MEDIANO: "Mediano",
    GRANDE: "Grande",
    INHOUSE: "Inhouse",
    SIN_CLASIFICAR: "Sin clasificar",
  };

  return labels[code] || code;
}

function money(value) {
  return n(value).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  });
}

function mdp(value) {
  return (
    "$" +
    (n(value) / 1000000).toLocaleString(
      "es-MX",
      {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      }
    ) +
    " mdp"
  );
}

function pct(value) {
  return `${n(value).toFixed(1)}%`;
}

function csvCell(value) {
  const text = String(value ?? "");

  return `"${text.replace(/"/g, '""')}"`;
}


// =====================================================================
// PCC_REVIEW_EXCEL_ENTERPRISE_V1_1
//
// Genera XLSX a partir del CSV YA EXISTENTE.
// No replica formulas ni filtros de negocio.
// =====================================================================

function pccParseCsv(text) {
  const rows = [];
  let currentRow = [];
  let currentCell = "";
  let quoted = false;

  const source = String(text || "").replace(/^\uFEFF/, "");

  for (let i = 0; i < source.length; i += 1) {
    const character = source[i];

    if (quoted) {
      if (character === '"') {
        if (source[i + 1] === '"') {
          currentCell += '"';
          i += 1;
        } else {
          quoted = false;
        }
      } else {
        currentCell += character;
      }

      continue;
    }

    if (character === '"') {
      quoted = true;
      continue;
    }

    if (character === ",") {
      currentRow.push(currentCell);
      currentCell = "";
      continue;
    }

    if (character === "\r") {
      continue;
    }

    if (character === "\n") {
      currentRow.push(currentCell);
      rows.push(currentRow);

      currentRow = [];
      currentCell = "";

      continue;
    }

    currentCell += character;
  }

  currentRow.push(currentCell);

  if (
    currentRow.length > 1 ||
    String(currentRow[0] || "").trim()
  ) {
    rows.push(currentRow);
  }

  return rows;
}

function pccExcelNumber(value) {
  if (
    value === null ||
    value === undefined
  ) {
    return null;
  }

  const raw = String(value)
    .trim()
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .replace(/%/g, "")
    .replace(/\s+/g, "");

  if (
    !raw ||
    raw === "-" ||
    raw === "—"
  ) {
    return null;
  }

  const result = Number(raw);

  return Number.isFinite(result)
    ? result
    : null;
}

function pccExcelText(value) {
  const text = String(value ?? "").trim();

  if (
    text === "SIN_MONTO" ||
    text === "SIN MONTO"
  ) {
    return "—";
  }

  return text;
}

function pccExcelHeaderKey(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toUpperCase()
    .trim();
}

function pccExcelStamp() {
  const now = new Date();

  const pad = (number) =>
    String(number).padStart(2, "0");

  return (
    now.getFullYear() +
    "-" +
    pad(now.getMonth() + 1) +
    "-" +
    pad(now.getDate()) +
    "_" +
    pad(now.getHours()) +
    pad(now.getMinutes())
  );
}

function pccDownloadExcel(buffer) {
  const blob = new Blob(
    [buffer],
    {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }
  );

  const url =
    URL.createObjectURL(blob);

  const anchor =
    document.createElement("a");

  anchor.href = url;

  anchor.download =
    "revision_ejecutiva_portafolio_" +
    pccExcelStamp() +
    ".xlsx";

  document.body.appendChild(anchor);

  anchor.click();
  anchor.remove();

  setTimeout(
    () => URL.revokeObjectURL(url),
    1000
  );
}

async function pccCaptureExistingCsv(exportCsv) {
  let capturedBlob = null;

  const originalCreateObjectURL =
    URL.createObjectURL;

  const originalRevokeObjectURL =
    URL.revokeObjectURL;

  const originalAnchorClick =
    HTMLAnchorElement.prototype.click;

  try {
    URL.createObjectURL =
      function captureObjectUrl(blob) {
        if (blob instanceof Blob) {
          capturedBlob = blob;
        }

        return "blob:pcc-captured-csv";
      };

    URL.revokeObjectURL =
      function suppressCapturedRevoke() {
        return undefined;
      };

    HTMLAnchorElement.prototype.click =
      function suppressLegacyDownload() {
        return undefined;
      };

    const result =
      exportCsv();

    if (
      result &&
      typeof result.then === "function"
    ) {
      await result;
    }
  } finally {
    URL.createObjectURL =
      originalCreateObjectURL;

    URL.revokeObjectURL =
      originalRevokeObjectURL;

    HTMLAnchorElement.prototype.click =
      originalAnchorClick;
  }

  if (!capturedBlob) {
    throw new Error(
      "No fue posible capturar el CSV actual."
    );
  }

  return capturedBlob.text();
}

async function pccExportPortfolioExcel(exportCsv) {
  const csvText =
    await pccCaptureExistingCsv(
      exportCsv
    );

  const csvRows =
    pccParseCsv(csvText);

  if (!csvRows.length) {
    throw new Error(
      "El CSV generado esta vacio."
    );
  }

  const blankRowIndex =
    csvRows.findIndex(
      (row) =>
        row.every(
          (cell) =>
            String(cell || "").trim() === ""
        )
    );

  if (blankRowIndex < 0) {
    throw new Error(
      "No se encontro la separacion entre metadatos y tabla."
    );
  }

  const metadataRows =
    csvRows
      .slice(0, blankRowIndex)
      .filter(
        (row) =>
          row.some(
            (cell) =>
              String(cell || "").trim() !== ""
          )
      );

  const dataRows =
    csvRows.slice(
      blankRowIndex + 1
    );

  if (!dataRows.length) {
    throw new Error(
      "El CSV no contiene tabla."
    );
  }

  const headers =
    dataRows[0].map(
      (value) =>
        String(value || "").trim()
    );

  const rows =
    dataRows
      .slice(1)
      .filter(
        (row) =>
          row.some(
            (cell) =>
              String(cell || "").trim() !== ""
          )
      );

  const metadata = {};

  metadataRows.forEach((row) => {
    const key =
      String(row[0] || "").trim();

    const value =
      String(row[1] || "").trim();

    if (key) {
      metadata[key] = value;
    }
  });

  const indexes = {};

  headers.forEach(
    (header, index) => {
      indexes[
        pccExcelHeaderKey(header)
      ] = index;
    }
  );

  const findColumn = (...names) => {
    for (const name of names) {
      const key =
        pccExcelHeaderKey(name);

      if (
        Object.prototype.hasOwnProperty.call(
          indexes,
          key
        )
      ) {
        return indexes[key];
      }
    }

    return -1;
  };

  const montoIndex =
    findColumn("Monto");

  const pipelineIndex =
    findColumn(
      "Pipeline ponderado",
      "Pipeline pond"
    );

  const porCobrarIndex =
    findColumn("Por cobrar");

  const roiIndex =
    findColumn("ROI");

  let totalMonto = 0;
  let totalPipeline = 0;
  let totalPorCobrar = 0;

  let roiWeightedValue = 0;
  let roiWeightedBase = 0;

  rows.forEach((row) => {
    const monto =
      montoIndex >= 0
        ? pccExcelNumber(
            row[montoIndex]
          )
        : null;

    const pipeline =
      pipelineIndex >= 0
        ? pccExcelNumber(
            row[pipelineIndex]
          )
        : null;

    const porCobrar =
      porCobrarIndex >= 0
        ? pccExcelNumber(
            row[porCobrarIndex]
          )
        : null;

    const roi =
      roiIndex >= 0
        ? pccExcelNumber(
            row[roiIndex]
          )
        : null;

    if (monto !== null) {
      totalMonto += monto;
    }

    if (pipeline !== null) {
      totalPipeline += pipeline;
    }

    if (porCobrar !== null) {
      totalPorCobrar += porCobrar;
    }

    if (
      roi !== null &&
      monto !== null &&
      monto > 0
    ) {
      roiWeightedValue +=
        roi * monto;

      roiWeightedBase +=
        monto;
    }
  });

  const roiPonderado =
    roiWeightedBase > 0
      ? roiWeightedValue /
        roiWeightedBase
      : null;

  const workbook =
    new ExcelJS.Workbook();

  workbook.creator =
    "IP DEPOT";

  workbook.company =
    "IP DEPOT";

  workbook.title =
    "Revision ejecutiva de portafolio";

  workbook.subject =
    "Portafolio ejecutivo";

  workbook.created =
    new Date();

  const sheet =
    workbook.addWorksheet(
      "Portafolio",
      {
        views: [
          {
            state: "frozen",
            xSplit: 2,
            ySplit: 12,
            showGridLines: false,
          },
        ],
        properties: {
          defaultRowHeight: 19,
        },
        pageSetup: {
          orientation: "landscape",
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          paperSize: 9,
          margins: {
            left: 0.25,
            right: 0.25,
            top: 0.4,
            bottom: 0.4,
            header: 0.2,
            footer: 0.2,
          },
        },
      }
    );

  const columnCount =
    Math.max(
      headers.length,
      15
    );

  const COLORS = {
    navy: "172033",
    teal: "167C6A",
    tealSoft: "EAF5F2",
    white: "FFFFFF",
    text: "172033",
    muted: "667085",
    soft: "F6F7F8",
    border: "DFE3E6",
    zebra: "FAFBFC",
    red: "B42318",
    redSoft: "FEECEB",
    amber: "9A6700",
    amberSoft: "FFF4D6",
    green: "087443",
    greenSoft: "E7F6EE",
  };

  // ===========================================================
  // TITULO
  // ===========================================================

  sheet.mergeCells(
    1,
    1,
    1,
    columnCount
  );

  const title =
    sheet.getCell(1, 1);

  title.value =
    "REVISIÓN EJECUTIVA DE PORTAFOLIO";

  title.font = {
    name: "Aptos Display",
    size: 20,
    bold: true,
    color: {
      argb: COLORS.white,
    },
  };

  title.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: {
      argb: COLORS.navy,
    },
  };

  title.alignment = {
    vertical: "middle",
    horizontal: "left",
  };

  sheet.getRow(1).height = 36;

  // ===========================================================
  // METADATOS
  // ===========================================================

  const metadataItems = [
    [
      "Generado",
      metadata.Generado ||
        new Date().toLocaleString(
          "es-MX"
        ),
    ],
    [
      "Búsqueda",
      metadata["Búsqueda"] ||
        metadata.Busqueda ||
        "Todas",
    ],
    [
      "Situación",
      metadata["Situación"] ||
        metadata.Situacion ||
        "ALL",
    ],
    [
      "Tamaño",
      metadata["Tamaño"] ||
        metadata.Tamano ||
        "ALL",
    ],
    [
      "ROI",
      metadata.ROI ||
        "ALL",
    ],
  ];

  metadataItems.forEach(
    ([label, value], index) => {
      const rowNumber =
        3 + index;

      const labelCell =
        sheet.getCell(
          rowNumber,
          1
        );

      const valueCell =
        sheet.getCell(
          rowNumber,
          2
        );

      labelCell.value =
        label;

      labelCell.font = {
        name: "Aptos",
        size: 9,
        bold: true,
        color: {
          argb: COLORS.muted,
        },
      };

      valueCell.value =
        value;

      valueCell.font = {
        name: "Aptos",
        size: 9,
        color: {
          argb: COLORS.text,
        },
      };
    }
  );

  // ===========================================================
  // KPIs
  // ===========================================================

  const kpis = [
    {
      column: 1,
      label:
        "PROYECTOS VISIBLES",
      value:
        rows.length,
      format:
        "0",
    },
    {
      column: 4,
      label:
        "MONTO DEL PORTAFOLIO",
      value:
        totalMonto,
      format:
        '[$$-es-MX]#,##0.00;[Red]-[$$-es-MX]#,##0.00',
    },
    {
      column: 7,
      label:
        "PIPELINE PONDERADO",
      value:
        totalPipeline,
      format:
        '[$$-es-MX]#,##0.00;[Red]-[$$-es-MX]#,##0.00',
    },
    {
      column: 10,
      label:
        "CONTRATADO POR COBRAR",
      value:
        totalPorCobrar,
      format:
        '[$$-es-MX]#,##0.00;[Red]-[$$-es-MX]#,##0.00',
    },
    {
      column: 13,
      label:
        "ROI PONDERADO",
      value:
        roiPonderado,
      format:
        '0.00"%"',
    },
  ];

  kpis.forEach((kpi) => {
    sheet.mergeCells(
      9,
      kpi.column,
      9,
      kpi.column + 1
    );

    sheet.mergeCells(
      10,
      kpi.column,
      10,
      kpi.column + 1
    );

    const label =
      sheet.getCell(
        9,
        kpi.column
      );

    const value =
      sheet.getCell(
        10,
        kpi.column
      );

    label.value =
      kpi.label;

    label.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: COLORS.soft,
      },
    };

    label.font = {
      name: "Aptos",
      size: 8,
      bold: true,
      color: {
        argb: COLORS.muted,
      },
    };

    label.alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    value.value =
      kpi.value === null
        ? "—"
        : kpi.value;

    value.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: COLORS.soft,
      },
    };

    value.font = {
      name: "Aptos Display",
      size: 14,
      bold: true,
      color: {
        argb: COLORS.text,
      },
    };

    value.alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    if (
      typeof kpi.value === "number"
    ) {
      value.numFmt =
        kpi.format;
    }

    label.border = {
      bottom: {
        style: "thin",
        color: {
          argb: COLORS.border,
        },
      },
    };

    value.border = {
      bottom: {
        style: "thin",
        color: {
          argb: COLORS.border,
        },
      },
    };
  });

  sheet.getRow(9).height = 20;
  sheet.getRow(10).height = 27;

  // ===========================================================
  // HEADERS
  // ===========================================================

  const headerRow = 12;

  headers.forEach(
    (header, columnIndex) => {
      const cell =
        sheet.getCell(
          headerRow,
          columnIndex + 1
        );

      cell.value =
        header;

      cell.font = {
        name: "Aptos",
        size: 8,
        bold: true,
        color: {
          argb: COLORS.white,
        },
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: COLORS.teal,
        },
      };

      cell.alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
      };
    }
  );

  sheet.getRow(
    headerRow
  ).height = 28;

  const moneyColumns =
    new Set([
      "MONTO",
      "PIPELINE POND",
      "PIPELINE PONDERADO",
      "INGRESOS",
      "COSTO REAL",
      "POR COBRAR",
      "UTILIDAD",
    ]);

  const percentageColumns =
    new Set([
      "MARGEN",
      "AVANCE DE COBRO",
      "ROI",
    ]);

  // ===========================================================
  // DATA
  // ===========================================================

  rows.forEach(
    (sourceRow, rowIndex) => {
      const rowNumber =
        headerRow +
        1 +
        rowIndex;

      headers.forEach(
        (header, columnIndex) => {
          const key =
            pccExcelHeaderKey(
              header
            );

          const sourceValue =
            sourceRow[
              columnIndex
            ];

          const cell =
            sheet.getCell(
              rowNumber,
              columnIndex + 1
            );

          if (
            moneyColumns.has(key)
          ) {
            const number =
              pccExcelNumber(
                sourceValue
              );

            cell.value =
              number;

            cell.numFmt =
              '[$$-es-MX]#,##0.00;[Red]-[$$-es-MX]#,##0.00';

            cell.alignment = {
              horizontal:
                "right",
              vertical:
                "middle",
            };
          } else if (
            percentageColumns.has(
              key
            )
          ) {
            const number =
              pccExcelNumber(
                sourceValue
              );

            cell.value =
              number;

            cell.numFmt =
              '0.00"%"';

            cell.alignment = {
              horizontal:
                "right",
              vertical:
                "middle",
            };
          } else {
            cell.value =
              pccExcelText(
                sourceValue
              );

            cell.alignment = {
              horizontal:
                "left",
              vertical:
                "middle",
              wrapText:
                key ===
                  "PROYECTO" ||
                key ===
                  "SIGUIENTE ACCION",
            };
          }

          cell.font = {
            name: "Aptos",
            size: 9,
            color: {
              argb: COLORS.text,
            },
          };

          if (
            rowIndex % 2 === 1
          ) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: {
                argb: COLORS.zebra,
              },
            };
          }

          cell.border = {
            bottom: {
              style: "hair",
              color: {
                argb: COLORS.border,
              },
            },
          };
        }
      );

      sheet.getRow(
        rowNumber
      ).height = 24;

      // ROI visual.
      if (roiIndex >= 0) {
        const roiCell =
          sheet.getCell(
            rowNumber,
            roiIndex + 1
          );

        const roi =
          pccExcelNumber(
            sourceRow[roiIndex]
          );

        if (roi !== null) {
          if (roi <= 0) {
            roiCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: {
                argb: COLORS.redSoft,
              },
            };

            roiCell.font = {
              name: "Aptos",
              size: 9,
              bold: true,
              color: {
                argb: COLORS.red,
              },
            };
          } else if (
            roi <= 10
          ) {
            roiCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: {
                argb: COLORS.amberSoft,
              },
            };

            roiCell.font = {
              name: "Aptos",
              size: 9,
              bold: true,
              color: {
                argb: COLORS.amber,
              },
            };
          } else {
            roiCell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: {
                argb: COLORS.greenSoft,
              },
            };

            roiCell.font = {
              name: "Aptos",
              size: 9,
              bold: true,
              color: {
                argb: COLORS.green,
              },
            };
          }
        }
      }
    }
  );

  // ===========================================================
  // AUTOFILTER
  // ===========================================================

  if (rows.length > 0) {
    sheet.autoFilter = {
      from: {
        row: headerRow,
        column: 1,
      },
      to: {
        row:
          headerRow +
          rows.length,
        column:
          headers.length,
      },
    };
  }

  // ===========================================================
  // WIDTHS
  // ===========================================================

  const widthMap = {
    PROYECTO: 40,
    CLIENTE: 20,
    SITUACION: 19,
    ETAPA: 22,
    TAMANO: 14,
    MONTO: 19,
    "PIPELINE POND": 19,
    "PIPELINE PONDERADO": 19,
    INGRESOS: 19,
    "COSTO REAL": 19,
    "POR COBRAR": 19,
    UTILIDAD: 19,
    MARGEN: 14,
    "AVANCE DE COBRO": 18,
    ROI: 14,
    RESPONSABLE: 20,
    "SIGUIENTE ACCION": 32,
  };

  headers.forEach(
    (header, index) => {
      const key =
        pccExcelHeaderKey(
          header
        );

      sheet.getColumn(
        index + 1
      ).width =
        widthMap[key] || 16;
    }
  );

  // ===========================================================
  // FORMATO EXTRA
  // ===========================================================

  sheet.getColumn(1).font = {
    name: "Aptos",
    size: 9,
    bold: true,
    color: {
      argb: COLORS.text,
    },
  };

  sheet.headerFooter.oddFooter =
    "&LIP DEPOT" +
    "&CRevisión ejecutiva de portafolio" +
    "&RPágina &P de &N";

  sheet.pageSetup.printTitlesRow =
    "12:12";

  // ===========================================================
  // EXPORTAR
  // ===========================================================

  const buffer =
    await workbook.xlsx.writeBuffer();

  pccDownloadExcel(
    buffer
  );
}

// PCC_REVIEW_EXCEL_ENTERPRISE_V1_1_END
export default function PortfolioReviewView({
  proyectos = [],
  apiRequest,
  onBack,
}) {
  const [financeById, setFinanceById] = useState({});
  const [loadingFinance, setLoadingFinance] = useState(true);

  const [
    financeErrors,
    setFinanceErrors,
  ] = useState([]);

  const [search, setSearch] = useState("");
  const [situationFilter, setSituationFilter] = useState(ALL);
  const [sizeFilter, setSizeFilter] = useState(ALL);
  const [roiFilter, setRoiFilter] = useState(ALL);

  const [sortKey, setSortKey] = useState("monto");
  const [sortDirection, setSortDirection] = useState("desc");

  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    let cancelled = false;

    const sleep = (ms) =>
      new Promise((resolve) =>
        setTimeout(resolve, ms)
      );

    async function requestFinanceSummary(id) {
      const path =
        `/proyectos/${encodeURIComponent(id)}/finanzas/resumen`;

      let lastError = null;

      for (let attempt = 1; attempt <= 3; attempt += 1) {
        try {
          const response = await apiRequest(
            path,
            {
              method: "GET",
            }
          );

          const summary =
            response?.resumen ||
            response?.item?.resumen;

          if (!summary || typeof summary !== "object") {
            throw new Error(
              "La API no devolvió un resumen financiero válido."
            );
          }

          return {
            ok: true,
            summary,
            attempts: attempt,
          };
        } catch (error) {
          lastError = error;

          console.warn(
            `[PortfolioReview] intento ${attempt}/3 falló para ${id}`,
            error
          );

          if (attempt < 3) {
            await sleep(
              250 * attempt
            );
          }
        }
      }

      return {
        ok: false,
        summary: null,
        error:
          lastError?.message ||
          "Resumen financiero no disponible",
        attempts: 3,
      };
    }

    async function loadFinance() {
      setLoadingFinance(true);
      setFinanceErrors([]);

      if (typeof apiRequest !== "function") {
        setLoadingFinance(false);
        return;
      }

      const map = {};
      const errors = [];

      /*
       * IMPORTANTE:
       * Se procesa secuencialmente.
       *
       * Antes se lanzaban todos los /finanzas/resumen
       * simultáneamente con Promise.all().
       *
       * Un fallo temporal terminaba convertido en {},
       * por lo que el KPI cambiaba entre refreshes.
       */
      for (const project of proyectos || []) {
        if (cancelled) {
          return;
        }

        const id =
          getProjectId(project);

        if (!id) {
          continue;
        }

        const result =
          await requestFinanceSummary(id);

        if (cancelled) {
          return;
        }

        if (result.ok) {
          map[id] = result.summary;
        } else {
          /*
           * NO escribir {}.
           *
           * La ausencia de clave significa:
           * resumen no disponible.
           *
           * Eso es distinto de un proyecto
           * cuyo resumen financiero realmente es cero.
           */
          errors.push({
            id,
            nombre:
              project?.nombre ||
              id,
            error:
              result.error,
          });
        }
      }

      if (cancelled) {
        return;
      }

      setFinanceById(map);
      setFinanceErrors(errors);
      setLoadingFinance(false);

      if (errors.length > 0) {
        console.warn(
          "[PortfolioReview] Resúmenes financieros no disponibles:",
          errors
        );
      } else {
        console.log(
          `[PortfolioReview] Finanzas cargadas correctamente: ${Object.keys(map).length} proyectos.`
        );
      }
    }

    loadFinance();

    return () => {
      cancelled = true;
    };
  }, [proyectos, apiRequest]);

  const rows = useMemo(() => {
    return (proyectos || [])
      .filter((project) => getProjectId(project))
      .map((project) => {
        const id = getProjectId(project);
        const financialLoaded =
          Object.prototype.hasOwnProperty.call(
            financeById,
            id
          );

        const financial =
          financialLoaded
            ? financeById[id]
            : {};

        const amount = projectAmount(project);
        const ingresos = n(financial?.ingresos);
        const invertido = n(financial?.invertido);
        const utilidad = n(financial?.utilidad);
        const porCobrar = n(financial?.porCobrar);
        const avanceCobro = n(financial?.avanceCobro);
        const margen = n(financial?.margen);

        const roi = financialRoi(financial);

        return {
          id,
          project,
          financial,
          financialLoaded,

          nombre:
            txt(project?.nombre) ||
            "Proyecto sin nombre",

          cliente:
            txt(project?.cliente) ||
            txt(project?.dependencia) ||
            "Sin cliente",

          responsable:
            txt(project?.responsable) ||
            "Sin responsable",

          situacion:
            situationOf(project),

          situacionLabel:
            situationLabel(
              situationOf(project)
            ),

          etapa:
            stageLabel(project),

          tamano:
            sizeCode(project),

          tamanoLabel:
            sizeLabel(project),

          monto: amount,

          pipeline:
            n(project?.pipelinePonderado),

          ingresos,
          invertido,
          utilidad,
          porCobrar,
          avanceCobro,
          margen,

          roi,
          roiBand: roiBand(roi),

          siguienteAccion:
            txt(
              project?.proximoPaso ??
              project?.siguienteAccion
            ) ||
            "Sin siguiente acción",

          prioridad:
            txt(project?.prioridad) ||
            "Sin prioridad",

          riesgo:
            txt(project?.riesgo) ||
            "Sin riesgo",
        };
      });
  }, [proyectos, financeById]);

  const filteredRows = useMemo(() => {
    const query = norm(search);

    let result = rows.filter((row) => {
      const matchesSearch =
        !query ||
        norm(row.nombre).includes(query) ||
        norm(row.cliente).includes(query) ||
        norm(row.responsable).includes(query);

      const matchesSituation =
        situationFilter === ALL ||
        row.situacion === situationFilter;

      const matchesSize =
        sizeFilter === ALL ||
        row.tamano === sizeFilter;

      const matchesRoi =
        roiFilter === ALL ||
        row.roiBand === roiFilter;

      return (
        matchesSearch &&
        matchesSituation &&
        matchesSize &&
        matchesRoi
      );
    });

    result = [...result].sort((a, b) => {
      let left;
      let right;

      switch (sortKey) {
        case "nombre":
          left = a.nombre;
          right = b.nombre;
          break;

        case "situacion":
          left = a.situacionLabel;
          right = b.situacionLabel;
          break;

        case "tamano":
          left = a.tamanoLabel;
          right = b.tamanoLabel;
          break;

        case "avance":
          left = a.avanceCobro;
          right = b.avanceCobro;
          break;

        case "roi":
          left = a.roi ?? -999999;
          right = b.roi ?? -999999;
          break;

        case "porCobrar":
          left = a.porCobrar;
          right = b.porCobrar;
          break;

        case "monto":
        default:
          left = a.monto;
          right = b.monto;
          break;
      }

      let compare = 0;

      if (
        typeof left === "string" ||
        typeof right === "string"
      ) {
        compare = String(left).localeCompare(
          String(right),
          "es"
        );
      } else {
        compare = n(left) - n(right);
      }

      return sortDirection === "asc"
        ? compare
        : -compare;
    });

    return result;
  }, [
    rows,
    search,
    situationFilter,
    sizeFilter,
    roiFilter,
    sortKey,
    sortDirection,
  ]);

  const totals = useMemo(() => {
    const totalMonto = filteredRows.reduce(
      (sum, row) => sum + row.monto,
      0
    );

    const pipeline = filteredRows.reduce(
      (sum, row) => sum + row.pipeline,
      0
    );

    const porCobrar = filteredRows.reduce(
      (sum, row) => sum + row.porCobrar,
      0
    );

    const utilidad = filteredRows.reduce(
      (sum, row) => sum + row.utilidad,
      0
    );

    const roiRows = filteredRows.filter(
      (row) =>
        row.roi !== null &&
        row.monto > 0
    );

    const roiBase = roiRows.reduce(
      (sum, row) => sum + row.monto,
      0
    );

    const roiWeighted =
      roiBase > 0
        ? roiRows.reduce(
            (sum, row) =>
              sum +
              row.roi * row.monto,
            0
          ) / roiBase
        : null;

    return {
      proyectos: filteredRows.length,
      monto: totalMonto,
      pipeline,
      porCobrar,
      utilidad,
      roi: roiWeighted,
    };
  }, [filteredRows]);

  const selected =
    rows.find(
      (row) =>
        row.id === selectedId
    ) || null;

  function changeSort(key) {
    if (sortKey === key) {
      setSortDirection(
        (current) =>
          current === "asc"
            ? "desc"
            : "asc"
      );

      return;
    }

    setSortKey(key);
    setSortDirection(
      key === "nombre"
        ? "asc"
        : "desc"
    );
  }

  function sortSymbol(key) {
    if (sortKey !== key) {
      return "";
    }

    return sortDirection === "asc"
      ? " ↑"
      : " ↓";
  }

  function clearFilters() {
    setSearch("");
    setSituationFilter(ALL);
    setSizeFilter(ALL);
    setRoiFilter(ALL);
  }

  function exportCsv() {
    const headers = [
      "Proyecto",
      "Cliente",
      "Situación",
      "Etapa",
      "Tamaño",
      "Monto",
      "Pipeline ponderado",
      "Ingresos",
      "Costo real",
      "Por cobrar",
      "Utilidad",
      "Margen",
      "Avance de cobro",
      "ROI",
      "Responsable",
      "Siguiente acción",
    ];

    const body = filteredRows.map((row) => [
      row.nombre,
      row.cliente,
      row.situacionLabel,
      row.etapa,
      row.tamanoLabel,
      row.monto,
      row.pipeline,
      row.ingresos,
      row.invertido,
      row.porCobrar,
      row.utilidad,
      row.margen,
      row.avanceCobro,
      row.roi ?? "",
      row.responsable,
      row.siguienteAccion,
    ]);

    const filterInfo = [
      ["Reporte", "Revisión ejecutiva de portafolio"],
      ["Generado", new Date().toLocaleString("es-MX")],
      ["Búsqueda", search || "Todas"],
      ["Situación", situationFilter],
      ["Tamaño", sizeFilter],
      ["ROI", roiFilter],
      [],
    ];

    const lines = [
      ...filterInfo.map(
        (row) =>
          row.map(csvCell).join(",")
      ),

      headers
        .map(csvCell)
        .join(","),

      ...body.map(
        (row) =>
          row.map(csvCell).join(",")
      ),
    ];

    const blob = new Blob(
      [
        "\uFEFF" +
        lines.join("\r\n"),
      ],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const anchor =
      document.createElement("a");

    anchor.href = url;

    anchor.download =
      `revision-portafolio-${
        new Date()
          .toISOString()
          .slice(0, 10)
      }.csv`;

    document.body.appendChild(anchor);

    anchor.click();

    anchor.remove();

    URL.revokeObjectURL(url);
  }

  return (
    <main className="prv-page">
      <header className="prv-header">
        <div>
          <span className="prv-eyebrow">
            VISTA DE REVISIÓN
          </span>

          <h1>
            Revisión ejecutiva de portafolio
          </h1>

          <p>
            
          </p>
        </div>

        <div className="prv-header-actions">
          <button
            type="button"
            className="prv-button secondary"
            onClick={onBack}
          >
            Volver a Dirección
          </button>

          <button
            type="button"
            className="prv-button primary"
            onClick={() => {
              pccExportPortfolioExcel(exportCsv).catch(
                (error) => {
                  console.error(
                    "No fue posible exportar Excel",
                    error
                  );

                  window.alert(
                    "No fue posible generar el archivo Excel."
                  );
                }
              );
            }}
            disabled={
              filteredRows.length === 0
            }
          >
            Exportar Excel
          </button>
        </div>
      </header>

      <section className="prv-kpis">
        <article>
          <span>
            Proyectos visibles
          </span>

          <strong>
            {totals.proyectos}
          </strong>

          <small>
            Según filtros aplicados
          </small>
        </article>

        <article>
          <span>
            Monto del portafolio
          </span>

          <strong>
            {mdp(totals.monto)}
          </strong>

          <small>
            Monto económico visible
          </small>
        </article>

        <article>
          <span>
            Pipeline ponderado
          </span>

          <strong>
            {mdp(totals.pipeline)}
          </strong>

          <small>
            Persistido por proyecto
          </small>
        </article>

        <article>
          <span>
            Contratado por cobrar
          </span>

          <strong>
            {
              loadingFinance
                ? "Calculando…"
                : financeErrors.length > 0
                  ? "Datos incompletos"
                  : mdp(totals.porCobrar)
            }
          </strong>

          <small>
            {
              loadingFinance
                ? "Consultando proyectos"
                : financeErrors.length > 0
                  ? `${financeErrors.length} resumen(es) pendientes`
                  : "Saldo financiero validado"
            }
          </small>
        </article>

        <article>
          <span>
            ROI ponderado
          </span>

          <strong>
            {
              totals.roi === null
                ? "Sin datos"
                : pct(totals.roi)
            }
          </strong>

          <small>
            Ponderado por monto
          </small>
        </article>
      </section>

      <section className="prv-workspace">

        {!loadingFinance && financeErrors.length > 0 && (
          <div className="prv-finance-integrity">
            <strong>
              Información financiera incompleta
            </strong>

            <span>
              {financeErrors.length} proyecto(s) no pudieron validar su resumen financiero después de 3 intentos. Los totales financieros no se presentan como definitivos.
            </span>
          </div>
        )}
        <div className="prv-toolbar">
          <div className="prv-toolbar-main">
            <label className="prv-search">
              <span>
                Buscar
              </span>

              <input
                type="search"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Proyecto, cliente o responsable"
              />
            </label>

            <label>
              <span>
                Situación
              </span>

              <select
                value={situationFilter}
                onChange={(event) =>
                  setSituationFilter(
                    event.target.value
                  )
                }
              >
                <option value={ALL}>
                  Todas
                </option>

                <option value="VENTA_ACTIVA">
                  Venta activa
                </option>

                <option value="EJECUCION">
                  En ejecución
                </option>

                <option value="CERRADO">
                  Cerrado
                </option>

                <option value="MUERTO">
                  Muerto
                </option>

                <option value="SIN_CLASIFICAR">
                  Sin clasificar
                </option>
              </select>
            </label>

            <label>
              <span>
                Tamaño
              </span>

              <select
                value={sizeFilter}
                onChange={(event) =>
                  setSizeFilter(
                    event.target.value
                  )
                }
              >
                <option value={ALL}>
                  Todos
                </option>

                <option value="CHICO">
                  Chico
                </option>

                <option value="MEDIANO">
                  Mediano
                </option>

                <option value="GRANDE">
                  Grande
                </option>

                <option value="INHOUSE">
                  Inhouse
                </option>

                <option value="SIN_CLASIFICAR">
                  Sin clasificar
                </option>
              </select>
            </label>

            <label>
              <span>
                ROI
              </span>

              <select
                value={roiFilter}
                onChange={(event) =>
                  setRoiFilter(
                    event.target.value
                  )
                }
              >
                <option value={ALL}>
                  Todas las bandas
                </option>

                <option value="NO_DATA">
                  Sin datos
                </option>

                <option value="NEGATIVE">
                  ROI ≤ 0
                </option>

                <option value="LOW">
                  0–10%
                </option>

                <option value="MEDIUM">
                  10–18%
                </option>

                <option value="HIGH">
                  &gt; 18%
                </option>
              </select>
            </label>
          </div>

          <div className="prv-toolbar-actions">
            <span className="prv-result-count">
              {filteredRows.length} de {rows.length} proyectos
            </span>

            <button
              type="button"
              className="prv-clear"
              onClick={clearFilters}
            >
              Limpiar filtros
            </button>
          </div>
        </div>

        <div className="prv-table-wrap">
          <table className="prv-table">
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("nombre")
                    }
                  >
                    Proyecto
                    {sortSymbol("nombre")}
                  </button>
                </th>

                <th>
                  Cliente
                </th>

                <th>
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("situacion")
                    }
                  >
                    Dinamicidad
                    {sortSymbol("situacion")}
                  </button>
                </th>

                <th>
                  Etapa
                </th>

                <th>
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("tamano")
                    }
                  >
                    Tamaño
                    {sortSymbol("tamano")}
                  </button>
                </th>

                <th className="numeric">
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("avance")
                    }
                  >
                    Cobrado
                    {sortSymbol("avance")}
                  </button>
                </th>

                <th className="numeric">
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("monto")
                    }
                  >
                    Monto
                    {sortSymbol("monto")}
                  </button>
                </th>

                <th className="numeric">
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("porCobrar")
                    }
                  >
                    Por cobrar
                    {sortSymbol("porCobrar")}
                  </button>
                </th>

                <th className="numeric">
                  <button
                    type="button"
                    onClick={() =>
                      changeSort("roi")
                    }
                  >
                    ROI
                    {sortSymbol("roi")}
                  </button>
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((row) => (
                <tr
                  key={row.id}
                  className={
                    selectedId === row.id
                      ? "is-selected"
                      : ""
                  }
                  onClick={() =>
                    setSelectedId(row.id)
                  }
                >
                  <td>
                    <strong className="prv-project-name">
                      {row.nombre}
                    </strong>

                    <small>
                      {row.responsable}
                    </small>
                  </td>

                  <td>
                    {row.cliente}
                  </td>

                  <td>
                    <span
                      className={
                        `prv-state state-${row.situacion.toLowerCase()}`
                      }
                    >
                      {row.situacionLabel}
                    </span>
                  </td>

                  <td>
                    {row.etapa}
                  </td>

                  <td>
                    <span
                      className={
                        `prv-size size-${row.tamano.toLowerCase()}`
                      }
                    >
                      {row.tamanoLabel}
                    </span>
                  </td>

                  <td className="numeric">
                    <div className="prv-flow-cell">
                      <strong>
                        {pct(row.avanceCobro)}
                      </strong>

                      <span>
                        <i
                          style={{
                            width:
                              `${Math.min(
                                100,
                                Math.max(
                                  0,
                                  row.avanceCobro
                                )
                              )}%`,
                          }}
                        />
                      </span>
                    </div>
                  </td>

                  <td className="numeric money">
                    {mdp(row.monto)}
                  </td>

                  <td className="numeric">
                    {
                      !row.financialLoaded
                        ? "Pendiente"
                        : row.porCobrar > 0
                          ? mdp(row.porCobrar)
                          : "—"
                    }
                  </td>

                  <td className="numeric">
                    <span
                      className={
                        `prv-roi roi-${row.roiBand.toLowerCase()}`
                      }
                    >
                      {
                        row.financialLoaded
                          ? roiLabel(row.roi)
                          : "Pendiente"
                      }
                    </span>
                  </td>
                </tr>
              ))}

              {filteredRows.length === 0 && (
                <tr>
                  <td
                    colSpan="9"
                    className="prv-empty"
                  >
                    No hay proyectos que coincidan con los filtros actuales.
                  </td>
                </tr>
              )}
            </tbody>

            {filteredRows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="6">
                    TOTAL VISTA ACTUAL
                    <small>
                      {filteredRows.length} proyectos
                    </small>
                  </td>

                  <td className="numeric">
                    {mdp(totals.monto)}
                  </td>

                  <td className="numeric">
                    {mdp(totals.porCobrar)}
                  </td>

                  <td className="numeric">
                    {
                      totals.roi === null
                        ? "—"
                        : pct(totals.roi)
                    }
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {loadingFinance && (
          <div className="prv-loading">
            Actualizando métricas financieras…
          </div>
        )}
      </section>

      {selected && (
        <>
          <button
            type="button"
            className="prv-drawer-backdrop"
            aria-label="Cerrar detalle"
            onClick={() =>
              setSelectedId("")
            }
          />

          <aside className="prv-drawer">
            <header>
              <div>
                <span>
                  DETALLE EJECUTIVO
                </span>

                <h2>
                  {selected.nombre}
                </h2>

                <p>
                  {selected.cliente}
                </p>
              </div>

              <button
                type="button"
                className="prv-drawer-close"
                onClick={() =>
                  setSelectedId("")
                }
              >
                ×
              </button>
            </header>

            <section className="prv-drawer-status">
              <span
                className={
                  `prv-state state-${selected.situacion.toLowerCase()}`
                }
              >
                {selected.situacionLabel}
              </span>

              <span
                className={
                  `prv-size size-${selected.tamano.toLowerCase()}`
                }
              >
                {selected.tamanoLabel}
              </span>

              <span
                className={
                  `prv-roi roi-${selected.roiBand.toLowerCase()}`
                }
              >
                ROI {roiLabel(selected.roi)}
              </span>
            </section>

            <section className="prv-drawer-finance">
              <article>
                <span>
                  Monto
                </span>

                <strong>
                  {money(selected.monto)}
                </strong>
              </article>

              <article>
                <span>
                  Cobrado
                </span>

                <strong>
                  {money(selected.ingresos)}
                </strong>
              </article>

              <article>
                <span>
                  Por cobrar
                </span>

                <strong>
                  {money(selected.porCobrar)}
                </strong>
              </article>

              <article>
                <span>
                  Costo real
                </span>

                <strong>
                  {money(selected.invertido)}
                </strong>
              </article>

              <article>
                <span>
                  Utilidad
                </span>

                <strong>
                  {money(selected.utilidad)}
                </strong>
              </article>

              <article>
                <span>
                  Margen
                </span>

                <strong>
                  {pct(selected.margen)}
                </strong>
              </article>
            </section>

            <section className="prv-drawer-progress">
              <div>
                <span>
                  Avance de cobro
                </span>

                <strong>
                  {pct(selected.avanceCobro)}
                </strong>
              </div>

              <div className="prv-progress">
                <i
                  style={{
                    width:
                      `${Math.min(
                        100,
                        Math.max(
                          0,
                          selected.avanceCobro
                        )
                      )}%`,
                  }}
                />
              </div>
            </section>

            <section className="prv-drawer-block">
              <h3>
                Posición del proyecto
              </h3>

              <dl>
                <div>
                  <dt>
                    Etapa
                  </dt>

                  <dd>
                    {selected.etapa}
                  </dd>
                </div>

                <div>
                  <dt>
                    Responsable
                  </dt>

                  <dd>
                    {selected.responsable}
                  </dd>
                </div>

                <div>
                  <dt>
                    Prioridad
                  </dt>

                  <dd>
                    {selected.prioridad}
                  </dd>
                </div>

                <div>
                  <dt>
                    Riesgo
                  </dt>

                  <dd>
                    {selected.riesgo}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="prv-drawer-action">
              <span>
                SIGUIENTE ACCIÓN
              </span>

              <strong>
                {selected.siguienteAccion}
              </strong>
            </section>

            <section className="prv-drawer-block">
              <h3>
                Lectura financiera
              </h3>

              <dl>
                <div>
                  <dt>
                    Pipeline ponderado
                  </dt>

                  <dd>
                    {money(selected.pipeline)}
                  </dd>
                </div>

                <div>
                  <dt>
                    ROI
                  </dt>

                  <dd>
                    {roiLabel(selected.roi)}
                  </dd>
                </div>
              </dl>
            </section>
          </aside>
        </>
      )}
    </main>
  );
}

// PCC_PORTFOLIO_REVIEW_ENTERPRISE_V1
// PCC_PORTFOLIO_REVIEW_FINANCE_STABLE_V1_END